import { useContext, useState, useEffect } from "react";
import {
  Box,
  Typography,
  alpha,
  TextField,
  InputAdornment,
  Grid,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { StepActions } from "../StepActions";
import { CacheContext } from "../../../Context/cacheContext";
import { VehicleContext } from "../../../Context/vehicleContext";

interface VehicleOptionsStepProps {
  onNext: () => void;
  onPrevious?: () => void;
  isFirstStep?: boolean;
  isLastStep?: boolean;
}

interface Facility {
  id: number;
  nume: string;
  [key: string]: any;
}

export default function VehicleOptionsStep({
  onNext,
  onPrevious,
  isFirstStep = false,
  isLastStep = false,
}: VehicleOptionsStepProps) {
  const cacheContext = useContext(CacheContext);
  const vehicleContext = useContext(VehicleContext) as any;

  const facilities: Facility[] = cacheContext.cache?.facilities || [];
  const currentVehicle =
    vehicleContext.state?.vehicle || vehicleContext.vehicle;

  // Initialize selected facilities from vehicle context (dotari is the backend field name)
  // Handle both array of IDs [1, 2, 3] and array of objects [{id: 1}, {id: 2}]
  const [selectedFacilities, setSelectedFacilities] = useState<Set<number>>(
    new Set(
      currentVehicle?.dotari
        ?.map((f: Facility | number) => (typeof f === "number" ? f : f?.id))
        .filter((id: number | undefined) => id !== undefined) || []
    )
  );

  // Search state
  const [searchQuery, setSearchQuery] = useState<string>("");

  // Filter facilities based on search query
  const filteredFacilities = facilities.filter((facility) =>
    facility.nume.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Update vehicle context whenever selection changes
  useEffect(() => {
    // Only update if we have facilities loaded from cache
    if (facilities.length === 0) {
      return;
    }

    const selectedFacilityObjects = facilities.filter((f) =>
      selectedFacilities.has(f.id)
    );

    if (vehicleContext.updateVehicle) {
      vehicleContext.updateVehicle({
        ...currentVehicle,
        dotari: selectedFacilityObjects,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFacilities, facilities]);

  const toggleFacility = (facilityId: number) => {
    setSelectedFacilities((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(facilityId)) {
        newSet.delete(facilityId);
      } else {
        newSet.add(facilityId);
      }
      return newSet;
    });
  };

  const clearAllSelections = () => {
    setSelectedFacilities(new Set());
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <TextField
            placeholder="Caută dotări..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              flex: 1,
              minWidth: 280,
              maxWidth: 400,
              bgcolor: "#fff",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                bgcolor: "#f8f9fa",
                "&:hover": {
                  bgcolor: "#fff",
                },
                "&:hover fieldset": {
                  borderColor: "#d0d5dd",
                },
                "&.Mui-focused": {
                  bgcolor: "#fff",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#FF6633",
                  borderWidth: "1px",
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#667085", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {selectedFacilities.size > 0 && (
              <Typography
                variant="body2"
                onClick={clearAllSelections}
                sx={{
                  color: "#667085",
                  cursor: "pointer",
                  "&:hover": { color: "#FF6633", textDecoration: "underline" },
                }}
              >
                Șterge toate
              </Typography>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 0.75,
                bgcolor: selectedFacilities.size > 0 ? "#FF6633" : "#f2f4f7",
                borderRadius: "20px",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: selectedFacilities.size > 0 ? "#fff" : "#667085",
                }}
              >
                {selectedFacilities.size}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: selectedFacilities.size > 0 ? "#fff" : "#667085",
                }}
              >
                selectate
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Selected facilities chips */}
        {selectedFacilities.size > 0 && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              p: 2,
              bgcolor: "#f8f9fa",
              borderRadius: "12px",
              border: "1px solid #e4e7ec",
            }}
          >
            {facilities
              .filter((f) => selectedFacilities.has(f.id))
              .map((facility) => (
                <Chip
                  key={facility.id}
                  label={facility.nume}
                  size="small"
                  onDelete={() => toggleFacility(facility.id)}
                  deleteIcon={
                    <CloseIcon
                      sx={{ fontSize: 14, color: "#667085 !important" }}
                    />
                  }
                  sx={{
                    bgcolor: "#fff",
                    color: "#344054",
                    fontSize: 13,
                    fontWeight: 500,
                    height: 28,
                    border: "1px solid #e4e7ec",
                    borderRadius: "6px",
                    "& .MuiChip-deleteIcon": {
                      "&:hover": { color: "#FF6633 !important" },
                    },
                    "&:hover": {
                      bgcolor: "#fff",
                      borderColor: "#d0d5dd",
                    },
                  }}
                />
              ))}
          </Box>
        )}
      </Box>

      {/* Options Grid */}
      <Box
        sx={{
          minHeight: 300,
          maxHeight: 450,
          overflowY: "auto",
          overflowX: "hidden",
          pr: 1,
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#d0d5dd",
            borderRadius: "3px",
            "&:hover": {
              background: "#98a2b3",
            },
          },
        }}
      >
        {filteredFacilities.length === 0 ? (
          <Box
            sx={{
              width: "100%",
              textAlign: "center",
              py: 8,
            }}
          >
            <Typography variant="body1" sx={{ color: "#667085" }}>
              {searchQuery
                ? "Nu s-au găsit dotări pentru căutarea ta"
                : "Nu există dotări disponibile în sistem"}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={1.5}>
            {filteredFacilities.map((facility) => {
              const isSelected = selectedFacilities.has(facility.id);
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={facility.id}>
                  <Box
                    onClick={() => toggleFacility(facility.id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: "10px",
                      cursor: "pointer",
                      bgcolor: isSelected ? alpha("#FF6633", 0.08) : "#fff",
                      border: `1.5px solid ${
                        isSelected ? "#FF6633" : "#e4e7ec"
                      }`,
                      transition: "all 0.15s ease",
                      "&:hover": {
                        bgcolor: isSelected
                          ? alpha("#FF6633", 0.12)
                          : "#f9fafb",
                        borderColor: isSelected ? "#FF6633" : "#d0d5dd",
                      },
                    }}
                  >
                    {isSelected ? (
                      <CheckCircleIcon
                        sx={{ color: "#FF6633", fontSize: 20 }}
                      />
                    ) : (
                      <RadioButtonUncheckedIcon
                        sx={{ color: "#d0d5dd", fontSize: 20 }}
                      />
                    )}
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: isSelected ? 500 : 400,
                        color: isSelected ? "#FF6633" : "#344054",
                        fontSize: 13,
                        lineHeight: 1.4,
                      }}
                    >
                      {facility.nume}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      <Box sx={{ mt: 3 }}>
        <StepActions
          onNext={onNext}
          onPrevious={onPrevious}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
        />
      </Box>
    </Box>
  );
}
